/**
 * clasificadores/preguntasRespuestas.js
 *
 * Clasificador del agente de Preguntas y Respuestas (Mercado Libre).
 *
 * Recibe:
 *   { kbItems, dashMetricas, publicaciones, config }
 *   (sin `ventas30d`: este clasificador no lee ventas, y su def del registry
 *   declara `diasVentas: 0` justamente por eso — report bssopFE0UcL3dujEWO8k)
 *
 * Devuelve:
 *   {
 *     secciones: {
 *       mejorar_publicacion: [...items con hallazgos],
 *       atender_horario:     [...franjas problemáticas],
 *     },
 *     analisis: { ... }
 *   }
 *
 * Cada item de "mejorar_publicacion" representa UNA publicación con hallazgos
 * de la IA (frases sugeridas, bullets, atributos faltantes). Cuando el agente
 * corre en modo "individual", crea 1 tarea por publicación. En modo "agrupado"
 * (default), crea 1 tarea con todas las publicaciones como recursos.
 */

// Umbral por defecto (min). Si la empresa configuró un SLA propio en
// `slaAtencion.ml.preguntasMin`, se usa ese (cargado en datos.config).
const UMBRAL_DEFAULT = 60

export function clasificarPreguntasRespuestas(datos) {
  // Ítem 16 — ver el comentario espejo en `opiniones.js`: un `kbItem` sin
  // clusters es el registro de fallo de la fase 2, no una publicación analizada.
  const kbItems = (Array.isArray(datos.kbItems) ? datos.kbItems : [])
    .filter(kb => Array.isArray(kb?.clusters) && kb.clusters.length > 0)
  const dashMetricas = datos.dashMetricas || null
  // Umbral SLA desde la empresa (datos.config = doc empresa) → fallback default.
  const slaConfigurado = datos.config?.slaAtencion?.ml?.preguntasMin
  const UMBRAL = (typeof slaConfigurado === 'number' && slaConfigurado > 0)
    ? slaConfigurado
    : UMBRAL_DEFAULT
  const publicacionesMap = new Map()
  for (const p of (datos.publicaciones || [])) {
    publicacionesMap.set(p.id, { title: p.title || p.titulo, permalink: p.permalink })
  }

  // ── Sección 1: Mejorar publicación ──
  // kbItems con al menos un hallazgo pendiente (frases, bullets o atributos).
  // Ordenados por cantidad de preguntas DESC (priorizamos lo que más impacta).
  const mejorar = []
  for (const kb of kbItems) {
    const nFrases = Array.isArray(kb.frasesSugeridas) ? kb.frasesSugeridas.length : 0
    const nBullets = Array.isArray(kb.bulletsSugeridos) ? kb.bulletsSugeridos.length : 0
    const nAttrs = Array.isArray(kb.atributosFaltantes) ? kb.atributosFaltantes.length : 0
    const totalHallazgos = nFrases + nBullets + nAttrs
    if (totalHallazgos === 0) continue

    const itemId = kb.itemId || (kb.id || '').split('_').slice(1).join('_')
    const tituloPub = kb.tituloItem || publicacionesMap.get(itemId)?.title || itemId
    const permalink = kb.permalinkItem || publicacionesMap.get(itemId)?.permalink || `https://articulo.mercadolibre.com.ar/${itemId}`
    const score = kb.scoreCalidadFicha != null ? kb.scoreCalidadFicha : null
    const oportunidad = score != null ? Math.max(0, 100 - score) : null
    const preguntas = kb.totalPreguntas || kb.cantidadPreguntas || 0

    mejorar.push({
      // Campos estándar que el agente / crearAccionable esperan:
      id: kb.id,
      _itemId: itemId,
      _docId: kb.id,
      _titulo: tituloPub,
      _permalink: permalink,
      // Payload con los hallazgos completos para que el operador no copie/pegue:
      hallazgos: {
        scoreCalidadFicha: score,
        scoreMotivo: kb.scoreMotivo || '',
        frasesSugeridas: kb.frasesSugeridas || [],
        bulletsSugeridos: kb.bulletsSugeridos || [],
        atributosFaltantes: kb.atributosFaltantes || [],
        bulletsConocimiento: kb.bulletsConocimiento || [],
        preguntasPrevistas: kb.preguntasPrevistas || [],
      },
      metricas: {
        preguntas,
        oportunidad,
        score,
        precio: kb.precioItem ?? null,
      },
      // Para la descripción de la tarea (modo individual o agrupado).
      _resumen: `${preguntas} preguntas · oportunidad ${oportunidad ?? '—'}/100 · 📝 ${nFrases} · 🔹 ${nBullets} · 🏷️ ${nAttrs}`,
    })
  }
  mejorar.sort((a, b) => (b.metricas.preguntas || 0) - (a.metricas.preguntas || 0))

  // ── Sección 2: Atender horario ──
  // Bandas + outliers con tiempo > umbral (alineado con el frontend).
  const horario = []
  if (dashMetricas) {
    // Las bandas se persisten dentro de `general` (ver preguntas-rollup.js).
    // Tolera ambos shapes por compat.
    const general = dashMetricas.general || dashMetricas
    const bandas = general.respuestaPromedioPorBanda || {}
    const horasLunVie = general.respuestaPromedioPorHoraLunVie || []

    const BANDAS = [
      { key: 'lunVieMadrugada', label: 'Lun-Vie Madrugada (00:00 – 08:59)' },
      { key: 'lunVieDia',       label: 'Lun-Vie Día (09:00 – 17:59)' },
      { key: 'lunVieNoche',     label: 'Lun-Vie Noche (18:00 – 23:59)' },
      { key: 'finDeSemana',     label: 'Fin de semana (Sáb + Dom)' },
    ]
    for (const b of BANDAS) {
      const data = bandas[b.key]
      if (!data || data.tiempoPromMin == null) continue
      if (data.tiempoPromMin <= UMBRAL) continue
      horario.push({
        id: `banda_${b.key}`,
        _itemId: `banda_${b.key}`,
        _docId: `banda_${b.key}`,
        _titulo: b.label,
        _permalink: null,
        tipo: 'banda',
        bandaKey: b.key,
        tiempoPromMin: data.tiempoPromMin,
        muestras: data.muestras || 0,
        gap: data.tiempoPromMin - UMBRAL,
        _resumen: `${Math.round(data.tiempoPromMin / 60 * 10) / 10}h promedio · ${data.muestras || 0} muestras`,
      })
    }

    // Outliers dentro de Lun-Vie Día solo si la banda en general está OK
    const bandaDiaOk = !bandas.lunVieDia || bandas.lunVieDia.tiempoPromMin == null
      || bandas.lunVieDia.tiempoPromMin <= UMBRAL
    if (bandaDiaOk && Array.isArray(horasLunVie)) {
      for (let h = 9; h < 18; h++) {
        const min = horasLunVie[h]
        if (min == null || min <= UMBRAL) continue
        horario.push({
          id: `outlier_${h}`,
          _itemId: `outlier_${h}`,
          _docId: `outlier_${h}`,
          _titulo: `Hora puntual ${String(h).padStart(2, '0')}:00 – ${String(h).padStart(2, '0')}:59 (Lun-Vie)`,
          _permalink: null,
          tipo: 'outlier',
          hora: h,
          tiempoPromMin: min,
          gap: min - UMBRAL,
          _resumen: `${min} min promedio (umbral ${UMBRAL})`,
        })
      }
    }

    horario.sort((a, b) => b.tiempoPromMin - a.tiempoPromMin)
  }

  return {
    secciones: {
      mejorar_publicacion: mejorar,
      atender_horario: horario,
    },
    analisis: {
      totalKbItems: kbItems.length,
      kbItemsConHallazgos: mejorar.length,
      franjasProblematicas: horario.length,
    },
  }
}
